// Le classement du catalogue des formations : les sujets, leur ordre, et les
// deux paliers d'un sujet rangé par niveau. Sorti de CoursesCatalog.jsx pour
// que la page /fr/formations et l'app mobile (/api/app/catalog) rangent les
// formations de la même façon : changer l'ordre ici le change aux deux endroits.
import { getAllCourses, getCourseBySlug } from "@/lib/courses-catalog";
import { COURSE_PITCHES } from "@/lib/coursePitches";

export const course = (slug, pitch) => ({ kind: "course", slug, pitch: pitch || COURSE_PITCHES[slug] });

// LE CATALOGUE, PAR SUJET. Chaque formation n'apparaît qu'une fois.
//
// Une première découpe séparait « Créer un site » (HTML, JavaScript, React) de
// « Programmer » (PHP, Symfony, Node, Git). LN : « tout ça, c'est pour créer un
// site ». C'était la frontière entre ce qui se voit et ce qui tourne sur le
// serveur, une logique de développeur que la page n'expliquait pas. La règle
// tient maintenant en une question, que tout le monde peut se poser : « ça sert
// à quoi ? ». À faire travailler l'IA et à automatiser, ou à créer un site ou
// une application. Dans le second sujet, le plus fourni, c'est le NIVEAU du
// catalogue qui range : ce qui se suit sans rien connaître d'abord, le reste
// ensuite.
export const SUBJECTS = [
    {
        id: "ia",
        tab: "IA et automatisation",
        title: "IA et automatisation",
        lead: "Faire travailler l'IA pour vous et automatiser ce qui se répète, en sachant ce qui se passe.",
        items: [
            course("formation-n8n", "Automatisez ce qui vous prend du temps, sans écrire de code."),
            course("formation-claude-code", "Faites écrire votre code par Claude, en comprenant ce qu'il produit."),
            course("formation-hermes-agent", "Un agent installé chez vous, qui vous répond depuis vos messageries."),
            course("formation-python", "Scripts, robots et analyse de données : le langage qui sert à tout automatiser."),
            course("formation-claude-design", "Des interfaces et des visuels professionnels, conçus avec Claude."),
            course("formation-claude-cowork", "Déléguez à Claude la rédaction, l'analyse et les tâches du quotidien."),
            course("formation-codex", "Codex lit votre projet et écrit les modifications, vous validez."),
        ],
    },
    {
        id: "site",
        tab: "Sites et applications",
        title: "Créer un site ou une application",
        lead: "De la première page à l'application complète : ce qui s'affiche, ce qui tourne derrière, et la mise en ligne.",
        byLevel: true,
        items: [
            course("formation-html", "Votre premier site, fait de vos mains, et un portfolio pour montrer votre travail."),
            course("mettre-son-site-en-ligne-facilement", "Votre site en ligne, avec une adresse e-mail à votre nom."),
            course("formation-javascript", "Rendez vos sites interactifs, en partant de zéro."),
            course("formation-git-github", "Gardez chaque version de votre site et revenez en arrière en un clic."),
            course("formation-php", "Stockez et gérez les données de vos sites."),
            course("formation-bootstrap", "Des pages professionnelles sans partir d'une feuille blanche."),
            course("formation-tailwindcss", "Des interfaces soignées, claires ou sombres, en peu de temps."),
            course("formation-sass", "Écrivez vos feuilles de style plus vite."),
            course("formation-typescript", "Un JavaScript plus sûr et plus facile à faire évoluer."),
            course("formation-mvc", "Organisez le code de vos sites pour qu'il dure."),
            course("formation-react", "Des interfaces modernes, apprises sur des projets concrets."),
            course("formation-nextjs", "Des sites complets, rapides et bien référencés."),
            course("formation-react-native", "Vos applications mobiles, écrites en JavaScript."),
            course("formation-symfony", "Un site e-commerce complet, du catalogue au paiement."),
            course("formation-nodejs", "Vos applications côté serveur, en JavaScript."),
            course("formation-angular", "Des applications web modernes avec Angular."),
        ],
    },
    {
        // Ce qui ne répond à aucune des deux questions. Pas d'onglet pour une
        // seule carte : on le voit en parcourant tout le catalogue.
        id: "aussi",
        title: "Et aussi",
        lead: "Pour ranger et protéger votre travail.",
        items: [course("maitriser-son-nas-synology", "Sauvegardes, cloud et partage : votre NAS bien réglé.")],
    },
];
export const TABS = SUBJECTS.filter((subject) => subject.tab);

// Rien ne doit manquer : une formation du catalogue qui ne serait rangée dans
// aucun sujet rejoint « Et aussi ».
const placed = new Set(SUBJECTS.flatMap((subject) => subject.items.map((i) => i.slug)));
getAllCourses().forEach((c) => {
    if (!placed.has(c.slug)) SUBJECTS[SUBJECTS.length - 1].items.push(course(c.slug));
});
// Dans chaque sujet : ce qui est disponible d'abord, ce qui arrive ensuite.
export const isSoon = (i) => Boolean(getCourseBySlug(i.slug)?.isSoon);
SUBJECTS.forEach((subject) => {
    subject.items = [...subject.items].sort((a, b) => isSoon(a) - isSoon(b));
});
// Les deux paliers d'un sujet rangé par niveau, d'après le niveau du catalogue.
export const tiers = (subject) => {
    const start = subject.items.filter((i) => !isSoon(i) && getCourseBySlug(i.slug).level === "beginner");
    const further = subject.items.filter((i) => !start.includes(i));
    return [
        {
            title: "Pour commencer",
            note: "Sans rien connaître avant.",
            items: start,
        },
        {
            title: "Pour aller plus loin",
            note: "Une fois les bases posées.",
            items: further,
        },
    ];
};
